import { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import AuthGuard from '@/router/AuthGuard';
import CsHome from '@/pages/CsHome';
import CsLogin from '@/pages/CsLogin';
import CsChat from '@/pages/CsChat';
import CsRestPassword from '../pages/CsResetPassword';
import UserList from '@/pages/UserList';
import CsAuthGuard from "@/router/CsAuthGuard.tsx";
import DingTalkTestPage from "@/pages/DingTalkTest";

// 懒加载页面组件
const LoginPage = lazy(() => import('@/pages/Login'));
const ChatBox = lazy(() => import('@/pages/Chat'));
const WebViewChat = lazy(() => import('@/pages/Chat/WebViewChat'));
const PublicPage = lazy(() => import('@/pages/Public'));
const AnalysisPage = lazy(() => import('@/pages/Analysis'));
const SurveyPage = lazy(() => import('@/pages/Survey'));
const MobileAnalysisPage = lazy(() => import('@/pages/MobileAnalysis'));

// 加载组件（使用 useTranslation hook 确保语言包已加载）
const PageLoading = () => {
  const { t } = useTranslation('common');
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="text-gray-500">{t('status.loading')}</div>
    </div>
  );
};

// 路由配置类型
export interface RouteConfig {
  path: string;
  element: React.ReactNode;
  children?: RouteConfig[];
  meta?: {
    title?: string;
    requiresAuth?: boolean;
  };
}

// 路由配置
export const routes: RouteConfig[] = [
  {
    path: '/login',
    element: <LoginPage />,
    meta: {
      title: '登录',
      requiresAuth: false,
    },
  },
  {
    path: '/chat',
    element: (
      <AuthGuard>
        <ChatBox />
      </AuthGuard>
    ),
    meta: {
      title: '聊天',
      requiresAuth: true,
    },
  },
  // WebView 入口路由（参数注入模式）
  {
    path: '/webview/chat',
    element: <WebViewChat />,
    meta: {
      title: '聊天',
      requiresAuth: false,
    },
  },
  // 公开页面示例（无需登录）
  {
    path: '/public',
    element: <PublicPage />,
    meta: {
      title: '公开页面',
      requiresAuth: false,
    },
  },
  // 全员AI创意大征集问卷（无需登录）
  // {
  //   path: '/survey',
  //   element: <SurveyPage />,
  //   meta: {
  //     title: '全员AI创意大征集',
  //     requiresAuth: false,
  //   },
  // },
  // // 营销监控看板
  // {
  //   path: '/analysis',
  //   element: <AnalysisPage />,
  //   meta: {
  //     title: '营销监控看板',
  //     requiresAuth: false,
  //   },
  // },
  // // 营销监控看板（移动端）
  // {
  //   path: '/mobile-analysis',
  //   element: <MobileAnalysisPage />,
  //   meta: {
  //     title: '营销监控看板',
  //     requiresAuth: false,
  //   },
  // },
  // 用户列表页面
  {
    path: '/userList',
    element: <UserList />,
    meta: {
      title: '用户列表',
      requiresAuth: false,
    },
  },
  // 客服登录页面
  {
    path: '/csLogin',
    element: <CsLogin />,
    meta: {
      title: '客服登录',
      requiresAuth: false,
    },
  },
  // 重置密码页面
  {
    path: '/csRestPassword',
    element:
        (<CsAuthGuard>
            <CsRestPassword />
        </CsAuthGuard>),

    meta: {
      title: '重置密码',
      requiresAuth: true,
    },
  },
  // 客服工作台页面
  {
    path: '/csHome',
    element:
        ( <CsAuthGuard>
            <CsHome />
        </CsAuthGuard>),
    meta: {
      title: '客服工作台',
      requiresAuth: true,
    },
  },
  // 客服对话页面
  {
    path: '/csChat',
    element:
        ( <CsAuthGuard>
            <CsChat />
        </CsAuthGuard>),
    meta: {
      title: '客服对话',
      requiresAuth: true,
    },
  },
    // 客服工作台页面
    // {
    //     path: '/newLogin',
    //     element: <NewLogin />,
    //     meta: {
    //         title: '新登录',
    //         requiresAuth: true,
    //     },
    // },
  {
    path: '/',
    element: <Navigate to="/login" replace />,
  },
  {
    path: '*',
    element: <Navigate to="/login" replace />,
  },
    {
        path: '/dingTalk-test',
        element:<DingTalkTestPage />
    },
];

// 包装懒加载组件
export const wrapWithSuspense = (routes: RouteConfig[]): RouteConfig[] => {
  return routes.map((route) => ({
    ...route,
    element: <Suspense fallback={<PageLoading />}>{route.element}</Suspense>,
    children: route.children ? wrapWithSuspense(route.children) : undefined,
  }));
};

export default routes;